/**
 * What one plan card says and offers, decided in one place.
 *
 * The server sends the facts — which plan is paid for, what each plan would
 * take away, how paying works here — and this turns them into a badge, a
 * button and the warnings a customer needs before pressing it. Kept pure so
 * the rules a customer depends on ("can I buy the plan I trialled?") are
 * tested rather than eyeballed.
 */

import { MODULE_LABELS, type ModuleKey } from '@cenox/shared';
import { currencyPrefix } from './format';

export interface PlanImpact {
  losesModules: string[];
  exceeds: Array<{ metric: 'users' | 'branches'; used: number; limit: number }>;
}

export interface PlanFacts {
  key: string;
  name: string;
  prices: Array<{ currencyCode: string; interval: string; amount: string }>;
  billingCurrency: string | null;
  modules: string[];
  limits?: Array<{ metric: string; limit: number | null; enforcement: string }>;
  isCurrent: boolean;
  state?: 'paid' | 'internal' | 'trial' | 'trial_ended' | 'lapsed' | null;
  includedForBusinessType?: string[];
  impact?: PlanImpact;
}

export interface CardContext {
  interval: string;
  pendingKey: string | null;
  pendingInterval: string | null;
  paymentMode: 'online' | 'manual';
  canPay: boolean;
}

export type CardAction =
  | { kind: 'current'; label: string }
  | { kind: 'contact'; label: string }
  | { kind: 'ask_admin'; label: string }
  /** Manual billing, invoice already open for this choice: show how to pay. */
  | { kind: 'instructions'; label: string }
  | { kind: 'pay'; label: string; needsConfirm: boolean };

export interface CardView {
  badge: { label: string; tone: 'info' | 'warning' | 'danger' | 'success' } | null;
  price: { amount: string; currencyCode: string } | null;
  modules: Array<{ key: string; label: string; note: string | null }>;
  limitsLine: string | null;
  warnings: string[];
  action: CardAction;
}

const label = (m: string): string => MODULE_LABELS[m as ModuleKey] ?? m.replace(/_/g, ' ');

const money = (amount: string, currency: string): string =>
  `${currencyPrefix(currency)}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export function planCardView(plan: PlanFacts, ctx: CardContext): CardView {
  const price =
    plan.prices.find((p) => p.interval === ctx.interval && p.currencyCode === plan.billingCurrency) ??
    null;

  const badge =
    plan.state === 'paid'
      ? ({ label: 'Current', tone: 'success' } as const)
      : plan.state === 'internal'
        ? ({ label: 'Internal use', tone: 'success' } as const)
        : plan.state === 'trial'
          ? ({ label: 'On trial', tone: 'info' } as const)
          : plan.state === 'trial_ended'
            ? ({ label: 'Trial ended', tone: 'danger' } as const)
            : plan.state === 'lapsed'
              ? ({ label: 'Lapsed', tone: 'danger' } as const)
              : null;

  const modules = [
    ...plan.modules.map((m) => ({ key: m, label: label(m), note: null })),
    // Held on every plan by a business of this type — a courier keeps
    // Deliveries on Starter — and saying nothing made Starter look like the
    // end of delivery recording.
    ...(plan.includedForBusinessType ?? []).map((m) => ({
      key: m,
      label: label(m),
      note: 'included for your type of business',
    })),
  ];

  /*
    What a plan differs by, which is now exactly two things: how many locations
    and how much monthly volume. Not users — a staff-account ceiling is not a
    plan difference any more, and a card still saying "Up to 3 users" would be
    selling a restriction that no longer exists.

    Read without filtering on enforcement. Locations `block` and transactions
    `warn`, and filtering on `block` — as this did — would have silently
    dropped the volume line the moment it was added.
  */
  const limit = (metric: string) => plan.limits?.find((l) => l.metric === metric)?.limit ?? null;
  const locations = limit('branches');
  const volume = limit('transactions');

  const count = (n: number) => n.toLocaleString();

  const limitsLine =
    locations === null && volume === null
      ? // No rows for either: negotiated, which is what Enterprise is.
        'Unlimited locations and transactions'
      : [
          locations !== null
            ? `${count(locations)} location${locations === 1 ? '' : 's'}`
            : 'Unlimited locations',
          volume !== null
            ? `${count(volume)} transactions a month`
            : 'Unlimited transactions',
        ].join(' · ');

  const impact = plan.impact ?? { losesModules: [], exceeds: [] };
  const warnings = [
    ...impact.losesModules.map((m) => `You would lose ${label(m)}.`),
    ...impact.exceeds.map(
      (e) =>
        `You have ${e.used} ${e.metric === 'users' ? 'users' : 'locations'}; this plan allows ${e.limit}. ` +
        `Nobody new can be added until you are under it.`,
    ),
  ];

  let action: CardAction;
  if (plan.isCurrent) {
    action = { kind: 'current', label: 'Your plan' };
  } else if (!price) {
    action = { kind: 'contact', label: 'Contact sales' };
  } else if (!ctx.canPay) {
    action = { kind: 'ask_admin', label: 'Ask an admin' };
  } else if (
    ctx.paymentMode === 'manual' &&
    ctx.pendingKey === plan.key &&
    ctx.pendingInterval === ctx.interval
  ) {
    // The invoice is already open. Pressing again used to open another one.
    action = { kind: 'instructions', label: 'How to pay' };
  } else {
    const amount = money(price.amount, price.currencyCode);
    action = {
      kind: 'pay',
      // The plan they trialled is a purchase like any other — never "Your plan".
      label: plan.state === 'trial' || plan.state === 'trial_ended' ? `Subscribe — ${amount}` : `Pay ${amount}`,
      needsConfirm: warnings.length > 0,
    };
  }

  return { badge, price, modules, limitsLine, warnings, action };
}
